import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Card, Collapse, Container, Row, Col, Badge } from 'react-bootstrap';
import { ApiServiceResponse, ApiService, ServiceFilterCriteria } from '../../interfaces';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Paragraph } from 'components/common/SimpleControls/Paragraph';
import { faChevronDown, faChevronUp, faCircle, faCube } from '@fortawesome/free-solid-svg-icons';


const ServicesTable: React.FC<{ servicesData: ApiServiceResponse; filterCriteria: ServiceFilterCriteria }> = ({ servicesData, filterCriteria }) => {
  // Group versions by service name
  const groupVersionsByService = (data: ApiService[]) => {
    return data.reduce((acc: Record<string, ApiService[]>, item: ApiService) => {
      if (!acc[item.name]) {
        acc[item.name] = [];
      }
      acc[item.name].push(item);
      return acc;
    }, {});
  };

  const filteredData = filterCriteria?.onlyInProd ? servicesData.result.filter(service => service.isActive) : servicesData.result;

  // Chronological order with most recent on the left
  const sortedData = [...filteredData].sort((a, b) => {
    if (!a.createdAt) {
      return 1;
    } else if (!b.createdAt) {
      return -1;
    }

    return new Date(b.createdAt.toString()).getTime() - new Date(a.createdAt.toString()).getTime();
  })

  const groupedServices = groupVersionsByService(sortedData);
  const [openService, setOpenService] = useState<string | null>(null);
  const { t } = useTranslation();

  // Format API date strings into readable localized dates
  const formatApiDate = (dateString: string) => {
    if (!dateString || dateString.startsWith("0001-01-01")) {
      return t("never");
    } else {
      return new Date(dateString).toLocaleString('fr-FR');
    }
  }

  // Get the status for each referentialService (In production, Deprecated or Not deployed)
  const getServiceStatus = (service: ApiService) => {
    if (service.isActive) {
      return { color: 'success', text: t("inProduction") }
    } else if (service.dateDeprecated && !service.dateDeprecated?.toString().startsWith("0001-01-01")) {
      return { color: 'warning', text: t("devices.services.deprecated") }
    }
    return { color: 'light', text: t("devices.services.noDeployed") }
  }


  const handleToggle = (serviceName: string) => {
    setOpenService(openService === serviceName ? null : serviceName);
  };

  return (
    <Container className="my-4">
      <Card.Header className="text-white fw-semibold text-center bg-secondary p-2 border-0 rounded-2 mb-4">
        <Card.Title className="mt-2 fs-2 text-center">{t("services.title.overview")}</Card.Title>
        <Paragraph className="mt-2 mb-0 text-center text-white">{t("services.title.subtitleServices")}</Paragraph>
      </Card.Header>
      {(Object.keys(groupedServices).length === 0) ? (
        <Paragraph className='text-center fs-5 fw-medium'>{t("devices.services.noServices")}</Paragraph>
      ) : (
        <>
          <Card className="d-flex flex-column gap-3 border-0 bg-transparent">
            {Object.keys(groupedServices).map((serviceName: string, index: number) => {
              const isOpen = openService === serviceName;
              const versionsCount = groupedServices[serviceName].length;
              return (
                <Card key={index} className='border-0 shadow-sm transition-all'>
                  <Col className="d-flex align-items-center justify-content-between px-3 clickable-row" onClick={() => handleToggle(serviceName)} aria-controls={`collapse-${serviceName}`} aria-expanded={isOpen}>
                    <div className='d-flex align-items-center gap-3'>
                      <Card className='bg-secondary text-white d-flex align-items-center justify-content-center rounded p-2'>
                        <FontAwesomeIcon icon={faCube} size='xl' />
                      </Card>
                      <Col>
                        <Card.Header className='bg-transparent border-0 mt-2 mb-0'>
                          <Card.Title className='fs-5 fw-bold text-dark mb-0 text-center'>{serviceName}</Card.Title>
                        </Card.Header>
                        <Paragraph className='text-secondary small'>{versionsCount} version{versionsCount > 1 ? 's' : ''} {t("available")}{versionsCount > 1 ? 's' : ''}</Paragraph>
                      </Col>
                    </div>
                    <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                  </Col>
                  <Collapse in={isOpen}>
                    <div id={`collapse-${serviceName}`}>
                      <div className='bg-light p-4 border-top'>
                        <Row className='g-3 justify-content-center'>
                          {groupedServices[serviceName].map((service: ApiService, versionIndex: number) => (
                            <Col md={versionsCount === 1 ? 12 : versionsCount === 2 ? 6 : versionsCount === 3 ? 4 : 3} key={versionIndex}>
                              <Card className='d-flex flex-column justify-content-between text-center border-0'>
                                <Col className='m-auto my-3'>
                                  {(() => {
                                    const status = getServiceStatus(service);
                                    return (
                                      <Badge bg={`${status.color}-subtle`} text={status.color} className='d-flex align-items-center gap-2 py-2 col-12'>
                                        <FontAwesomeIcon icon={faCircle} />
                                        <Paragraph className='fw-bold m-0'>{status.text}</Paragraph>
                                      </Badge>
                                    );
                                  })()}
                                </Col>
                                <Row className='d-flex justify-content-between align-items-start'>
                                  <Col>
                                    <Paragraph className='text-uppercase text-muted fw-medium mb-0'>{t("services.header.version")}</Paragraph>
                                    <Paragraph className='fw-semibold fs-6'>{service.version}</Paragraph>
                                  </Col>
                                  <Col>
                                    <Paragraph className='text-uppercase text-muted fw-medium mb-0 text-nowrap'>{t("services.header.externalPort")}</Paragraph>
                                    <Paragraph className='fw-semibold fs-6'>{service.defaultExternalPort}</Paragraph>
                                  </Col>
                                </Row>
                                <Row className='d-flex justify-content-between align-items-start'>
                                  <Col>
                                    <Paragraph className='text-uppercase text-muted fw-medium mb-0 text-nowrap'>{t("devices.services.createdAt")}</Paragraph>
                                    <Paragraph className='fw-semibold fs-6'>{formatApiDate(service.createdAt?.toString())}</Paragraph>
                                  </Col>
                                  <Col>
                                    <Paragraph className='text-uppercase text-muted fw-medium mb-0 text-nowrap'>{t("devices.services.dateInProd")}</Paragraph>
                                    <Paragraph className='fw-semibold fs-6'>{formatApiDate(service.dateInProd?.toString())}</Paragraph>
                                  </Col>
                                </Row>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </div>
                  </Collapse>
                </Card>
              );
            })}
          </Card>
        </>
      )}
    </Container>
  );
};

export default ServicesTable;
